import { Link, useLocation } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

import { KeyValueGrid } from "@/components/console/KeyValueGrid";
import { PageHeader } from "@/components/console/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageTransition } from "@/components/wizard/PageTransition";
import { useBridgeStatus } from "@/hooks/wizardQueries";
import { useWizardSession } from "@/hooks/useWizardSession";
import { formatTimestamp } from "@/lib/format";
import { strings } from "@/lib/strings";
import { wizardRoutePath } from "@/lib/steps";

interface DonePageLocationState {
  finalized?: boolean;
  authOverridesPath?: string;
}

export function DonePage() {
  const session = useWizardSession();
  const status = useBridgeStatus();
  const location = useLocation();
  const result = session.applyResult;
  const target =
    result?.organizationName ?? status.data?.organizationName ?? status.data?.organizationId ?? "Zitadel";
  const navState = (location.state ?? null) as DonePageLocationState | null;
  const finalized =
    navState?.finalized === true || new URLSearchParams(location.search).get("finalized") === "1";

  return (
    <PageTransition routeKey="done">
      <PageHeader
        title={strings.pages.done.title}
        actions={
          <>
            <Badge variant="success" className="font-mono uppercase">
              {strings.pages.done.badge}
            </Badge>
            <Button variant="outline" asChild>
              <Link to={wizardRoutePath("review")}>{strings.pages.done.restartBootstrap}</Link>
            </Button>
          </>
        }
      />

      <p className="text-sm text-muted-foreground">
        {result
          ? strings.pages.done.summaryWithCounts(
              result.provisionedCredentials.length,
              result.operations.length,
              target,
            )
          : strings.pages.done.summary(target)}
      </p>

      <KeyValueGrid
        items={[
          { label: "Organization", value: result?.organizationName ?? status.data?.organizationName ?? "—" },
          { label: "Project", value: result?.projectName ?? status.data?.projectName ?? "—" },
          {
            label: "Last apply",
            value: formatTimestamp(result?.generatedAt ?? status.data?.lastBootstrapCompletedAt),
          },
        ]}
        columns={3}
      />

      <Card className={finalized ? "border-emerald-500/40 bg-emerald-500/5 panel" : "panel"}>
        <CardContent className="space-y-2 p-6 text-sm">
          <h2 className="flex items-center gap-2 font-semibold">
            <ShieldCheck className="size-4 text-emerald-500" />
            {finalized ? strings.pages.done.authFinalizedTitle : strings.pages.done.authPendingTitle}
          </h2>
          <p className="text-muted-foreground">
            {finalized ? strings.pages.done.authFinalizedBody : strings.pages.done.authPendingBody}
            {finalized && navState?.authOverridesPath ? (
              <>
                {" "}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">{navState.authOverridesPath}</code>
              </>
            ) : null}
          </p>
          <p className="text-muted-foreground">{strings.pages.done.patNote}</p>
        </CardContent>
      </Card>

      <Card className="panel">
        <CardContent className="space-y-3 p-6 text-sm">
          <h2 className="font-semibold">{strings.pages.done.operatorTitle}</h2>
          <ul className="list-inside list-disc space-y-1 text-muted-foreground">
            {strings.pages.done.operatorItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <pre className="overflow-x-auto rounded-lg border border-border/60 bg-muted/30 p-3 font-mono text-xs">
            {strings.pages.done.restartExample}
          </pre>
        </CardContent>
      </Card>
    </PageTransition>
  );
}
